import { useNavigate } from 'react-router-dom';
import { Trash2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useMarkNotificationAsRead, useDeleteNotification } from '@/hooks/use-notifications';
import { formatRelativeTime, cn } from '@/lib/utils';
import type { Notification, NotificationType } from '@/types';

interface NotificationItemProps {
  notification: Notification;
  compact?: boolean;
  showDelete?: boolean;
  onClose?: () => void;
}

function getNotificationIcon(type: NotificationType): string {
  switch (type) {
    case 'task_assigned':
      return '📋';
    case 'task_updated':
      return '✏️';
    case 'comment_added':
      return '💬';
    case 'file_uploaded':
      return '📎';
    case 'mention':
      return '@';
    case 'due_soon':
      return '⏰';
    default:
      return '🔔';
  }
}

export function NotificationItem({
  notification,
  compact = false,
  showDelete = false,
  onClose,
}: NotificationItemProps) {
  const navigate = useNavigate();
  const markAsRead = useMarkNotificationAsRead();
  const deleteNotification = useDeleteNotification();

  const handleClick = () => {
    if (!notification.isRead) {
      markAsRead.mutate(notification.id);
    }

    if (notification.entityType === 'task' && notification.entityId) {
      onClose?.();
      navigate(`/tasks/${notification.entityId}`);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    deleteNotification.mutate(notification.id);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClick();
        }
      }}
      className={cn(
        'flex items-start gap-3 p-3 rounded-md cursor-pointer transition-colors',
        'hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        !notification.isRead && 'bg-primary/5',
        compact ? 'py-2' : 'py-3'
      )}
    >
      <div
        className={cn(
          'flex items-center justify-center rounded-full bg-secondary shrink-0',
          compact ? 'h-8 w-8 text-sm' : 'h-10 w-10 text-lg'
        )}
      >
        {getNotificationIcon(notification.type)}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <p
              className={cn(
                'font-medium truncate',
                compact ? 'text-sm' : 'text-base'
              )}
            >
              {notification.title}
            </p>
            {notification.message && (
              <p
                className={cn(
                  'text-muted-foreground',
                  compact ? 'text-xs line-clamp-1' : 'text-sm line-clamp-2'
                )}
              >
                {notification.message}
              </p>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              {formatRelativeTime(notification.createdAt)}
            </p>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            {!notification.isRead && (
              <div className="h-2 w-2 rounded-full bg-primary" />
            )}
            {showDelete && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-destructive"
                onClick={handleDelete}
                disabled={deleteNotification.isPending}
              >
                {deleteNotification.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
